import {
  CoordinateResult,
  MultiPolygon,
  Plane,
  Point,
  ResultType,
} from '../../geometric-types';
import { BoundingBoxPosition } from '../bounding-box';

interface BoundingBoxPoints {
  minX: number;
  minY: number;
  minZ: number;
  maxX: number;
  maxY: number;
  maxZ: number;
  minXPoint: [number, number, number];
  minYPoint: [number, number, number];
  minZPoint: [number, number, number];
  maxXPoint: [number, number, number];
  maxYPoint: [number, number, number];
  maxZPoint: [number, number, number];
}

function findBoundingBoxPoints(vertices: Point[]): BoundingBoxPoints {
  const result: BoundingBoxPoints = {
    minX: Infinity,
    minY: Infinity,
    minZ: Infinity,
    maxX: -Infinity,
    maxY: -Infinity,
    maxZ: -Infinity,
    minXPoint: [0, 0, 0],
    minYPoint: [0, 0, 0],
    minZPoint: [0, 0, 0],
    maxXPoint: [0, 0, 0],
    maxYPoint: [0, 0, 0],
    maxZPoint: [0, 0, 0],
  };

  for (const vertex of vertices) {
    const [x, y, z] = vertex;

    if (x < result.minX) {
      result.minX = x;
      result.minXPoint = vertex;
    }
    if (y < result.minY) {
      result.minY = y;
      result.minYPoint = vertex;
    }
    if (z < result.minZ) {
      result.minZ = z;
      result.minZPoint = vertex;
    }
    if (x > result.maxX) {
      result.maxX = x;
      result.maxXPoint = vertex;
    }
    if (y > result.maxY) {
      result.maxY = y;
      result.maxYPoint = vertex;
    }
    if (z > result.maxZ) {
      result.maxZ = z;
      result.maxZPoint = vertex;
    }
  }

  return result;
}

function getCoordinateResult(
  points: BoundingBoxPoints,
  position: BoundingBoxPosition,
  globalOffset: Point
): CoordinateResult {
  switch (position) {
    case BoundingBoxPosition.MIN_X:
      return {
        type: ResultType.COORDINATE,
        point: points.minXPoint,
        value: points.minX + globalOffset[0],
      };
    case BoundingBoxPosition.MIN_Y:
      return {
        type: ResultType.COORDINATE,
        point: points.minYPoint,
        value: points.minY + globalOffset[1],
      };
    case BoundingBoxPosition.MIN_Z:
      return {
        type: ResultType.COORDINATE,
        point: points.minZPoint,
        value: points.minZ + globalOffset[2],
      };
    case BoundingBoxPosition.MAX_X:
      return {
        type: ResultType.COORDINATE,
        point: points.maxXPoint,
        value: points.maxX + globalOffset[0],
      };
    case BoundingBoxPosition.MAX_Y:
      return {
        type: ResultType.COORDINATE,
        point: points.maxYPoint,
        value: points.maxY + globalOffset[1],
      };
    case BoundingBoxPosition.MAX_Z:
      return {
        type: ResultType.COORDINATE,
        point: points.maxZPoint,
        value: points.maxZ + globalOffset[2],
      };
  }
}

export const calculateShapeBoundingBox = (
  polygon: MultiPolygon,
  plane: Plane,
  position: BoundingBoxPosition,
  globalOffset: Point,
  thickness?: number
): CoordinateResult => {
  // Convert all exterior vertices to 3D points
  const allVertices: Point[] = polygon.polygons.flatMap(
    (poly) => poly.exterior
  );

  // If thickness is provided, create extruded vertices
  if (thickness !== undefined) {
    const extrudedVertices = allVertices.map((vertex) => {
      const [x, y, z] = vertex;
      // Extrude along the plane's normal vector
      const [nx, ny, nz] = plane.unitNormal;
      return [
        x + nx * thickness,
        y + ny * thickness,
        z + nz * thickness,
      ] as Point;
    });
    allVertices.push(...extrudedVertices);
  }

  const points = findBoundingBoxPoints(allVertices);
  return getCoordinateResult(points, position, globalOffset);
};

export const calculateLineShapeBoundingBox = (
  points: Point[],
  position: BoundingBoxPosition,
  globalOffset: Point
): CoordinateResult => {
  const boundingBoxPoints = findBoundingBoxPoints(points);
  return getCoordinateResult(boundingBoxPoints, position, globalOffset);
};
